import styled from "@emotion/styled";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Grid, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FormRadioGroup, FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormDatePicker } from "@zfp-akademie-a-s/zfp-design/form/date-picker";
import { type FamilyMemberFormModel, familyMemberFormSchema } from "Forms/Schemas/familyMemberFormSchema";
import { toRadioOptions } from "Forms/fieldOptions";
import {
  getGetAnalysisClientsQueryKey,
  useClientDetail,
  useCreateClient,
  useDeleteClient,
  useUpdateClient,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { CreateRelatedClientBodyAgeCategoryType, CreateRelatedClientBodyType, RelatedClientDetailType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { CLIENT_DETAIL_RETRY, getMinBirthDate, selectClientDetailClient } from "Infrastructure/Utils/ClientUtils";
import { useContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";

type Props = {
  readonly clientId?: string;
  readonly onClose: () => void;
};

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

export const FamilyMemberForm: React.FC<Props> = ({ clientId, onClose }) => {
  const isNewClient = !clientId;

  const { t } = useResource();

  const { readOnly } = useContext(AnalysisDetailContext);

  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { isValid },
  } = useForm<FamilyMemberFormModel>({
    resolver: yupResolver(familyMemberFormSchema(t)),
    mode: "all",
  });

  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const queryClient = useQueryClient();
  const { data: clientData } = useClientDetail(clientId ?? "", {
    query: { select: selectClientDetailClient, retry: CLIENT_DETAIL_RETRY },
  });

  const { mutateAsync: createClient } = useCreateClient();
  const { mutateAsync: updateClient } = useUpdateClient();
  const { mutateAsync: deleteClient } = useDeleteClient();

  const invalidateFamilyQueries = () => {
    queryClient.invalidateQueries({ queryKey: getGetAnalysisClientsQueryKey(analysisId, versionId) });
  };

  const watchAgeCategoryType = watch("ageCategoryType");

  useEffect(() => {
    if (clientData && clientData.type === RelatedClientDetailType.related) {
      setValue("firstName", clientData.firstName);
      setValue("lastName", clientData.surname);
      setValue("birthDate", clientData.birthDate || ("" as any));
      setValue("ageCategoryType", clientData.ageCategoryType);

      trigger();
    }
  }, [clientData, trigger, setValue]);

  const onSubmitNew = async (data: FamilyMemberFormModel) => {
    try {
      const isPlannedChild = data.ageCategoryType === CreateRelatedClientBodyAgeCategoryType.plannedChild;

      await createClient({
        data: {
          type: CreateRelatedClientBodyType.related,
          name: data.firstName,
          surname: data.lastName,
          birthDate: isPlannedChild ? null : data.birthDate instanceof Date ? data.birthDate.toISOString() : data.birthDate || null,
          ageCategoryType: data.ageCategoryType as CreateRelatedClientBodyAgeCategoryType,
          analysisId,
          versionId,
        },
      });
      toast.success("Člen rodiny úspěšně vytvořen.");
      invalidateFamilyQueries();
      onClose();
    } catch {
      toast.error("Člena rodiny se nepodařilo vytvořit.");
    }
  };

  const onSubmitUpdate = async (data: FamilyMemberFormModel) => {
    try {
      if (!clientId) throw new Error();

      const isPlannedChild = data.ageCategoryType === CreateRelatedClientBodyAgeCategoryType.plannedChild;

      await updateClient({
        clientGuid: clientId,
        data: {
          name: data.firstName,
          surname: data.lastName,
          birthDate: isPlannedChild ? null : data.birthDate instanceof Date ? data.birthDate.toISOString() : data.birthDate || null,
          ageCategoryType: data.ageCategoryType as CreateRelatedClientBodyAgeCategoryType,
        },
      });
      toast.success("Člen rodiny úspěšně upraven.");
      invalidateFamilyQueries();
      onClose();
    } catch {
      toast.error("Člena rodiny se nepodařilo vytvořit.");
    }
  };

  const onDelete = async () => {
    try {
      if (!clientId) throw new Error();

      await deleteClient({ clientGuid: clientId });
      toast.success("Člen rodiny úspěšně smazán.");
      invalidateFamilyQueries();
      onClose();
    } catch {
      toast.error("Člena rodiny se nepodařilo smazat.");
    }
  };

  return (
    <StyledForm>
      <Box>
        <Grid container spacing={Spacing.XL}>
          <Grid size={12}>
            <Typography mb={Spacing.M} variant="subtitle1" fontWeight={600}>
              Osobní údaje
            </Typography>
          </Grid>
          <Grid size={6}>
            <FormTextField
              control={control}
              name="firstName"
              label={t(Resources.AnalysisDetail.Family.Form.FirstName)}
              required
              disabled={readOnly}
              fullWidth
            />
          </Grid>
          <Grid size={6}>
            <FormTextField
              control={control}
              name="lastName"
              label={t(Resources.AnalysisDetail.Family.Form.LastName)}
              required
              disabled={readOnly}
              fullWidth
            />
          </Grid>
          <Grid size={12}>
            <FormRadioGroup
              control={control}
              name="ageCategoryType"
              label={t(Resources.AnalysisDetail.Family.Form.MemberType)}
              options={toRadioOptions([
                {
                  code: CreateRelatedClientBodyAgeCategoryType.adult,
                  name: t(Resources.AnalysisDetail.Family.Form.MemberTypes.Adult),
                },
                {
                  code: CreateRelatedClientBodyAgeCategoryType.child,
                  name: t(Resources.AnalysisDetail.Family.Form.MemberTypes.Child),
                },
                {
                  code: CreateRelatedClientBodyAgeCategoryType.plannedChild,
                  name: t(Resources.AnalysisDetail.Family.Form.MemberTypes.PlannedChild),
                },
              ])}
              defaultValue={CreateRelatedClientBodyAgeCategoryType.adult}
              columns={2}
              disabled={readOnly}
            />
          </Grid>
          {watchAgeCategoryType !== CreateRelatedClientBodyAgeCategoryType.plannedChild && (
            <Grid size={12}>
              <FormDatePicker
                control={control}
                name="birthDate"
                label={t(Resources.NewAnalysis.Form.Field.BirthDate)}
                required
                minDate={getMinBirthDate()}
                disableFuture
                disabled={readOnly}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
          )}
        </Grid>
      </Box>
      <Box
        pt={Spacing.XL}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          textAlign: "right",
        }}
      >
        {!isNewClient && (
          <Button
            disabled={readOnly}
            variant="text"
            color="error"
            sx={{
              marginRight: Spacing.XL,
            }}
            onClick={onDelete}
          >
            Smazat
          </Button>
        )}
        <Box display="flex">
          <Button
            variant="contained"
            color="secondary"
            sx={{
              marginRight: Spacing.XL,
            }}
            onClick={onClose}
          >
            Zavřít
          </Button>
          <Button
            variant="contained"
            disabled={!isValid || readOnly}
            onClick={handleSubmit(isNewClient ? onSubmitNew : onSubmitUpdate)}
            type="submit"
          >
            {isNewClient ? "Vytvořit" : "Uložit"}
          </Button>
        </Box>
      </Box>
    </StyledForm>
  );
};
